"use client";
import React from "react";
import Image from "next/image";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import styles from "./focusAreas.module.css";

export interface FocusAreaItem {
  id: string;
  title: string;
  description: string;
  icon?: string;
  iconAlt: string;
}

export interface FocusAreasProps {
  title?: string;
  subtitle?: string;
  focusAreas?: FocusAreaItem[];
}

const fallbackFocusAreas: FocusAreaItem[] = [
  {
    id: "agile-delivery",
    title: "Agile Delivery",
    description:
      "Sprint planning, refinement, and release management with proven frameworks",
    icon: "/icons/bars-progress.svg",
    iconAlt: "Agile Delivery",
  },
  {
    id: "team-leadership",
    title: "Team Leadership",
    description:
      "Engineering management, staffing, performance tracking, and career development",
    icon: "/icons/leadership.svg",
    iconAlt: "Team Leadership",
  },
  {
    id: "product-management",
    title: "Product Management",
    description:
      "Roadmaps, user stories, prioritization, and stakeholder alignment",
    icon: "/icons/productdel.svg",
    iconAlt: "Product Management",
  },
  {
    id: "front-end-engineering",
    title: "Front End Engineering",
    description:
      "React, Next.js, and modern web application development practices",
    icon: "/icons/code-browser.svg",
    iconAlt: "Front End Engineering",
  },
  {
    id: "devops-practice",
    title: "DevOps Practice",
    description:
      "CI/CD pipelines, automated testing, and deployment strategies",
    icon: "/icons/branching.svg",
    iconAlt: "DevOps Practice",
  },
  {
    id: "tooling-automation",
    title: "Tooling and Automation",
    description: "Azure DevOps, ClickUp configuration, and workflow automation",
    icon: "/icons/gears.svg",
    iconAlt: "Tooling and Automation",
  },
  {
    id: "ai-support",
    title: "AI Support for Teams",
    description:
      "AI-backed tools, prompt libraries, and intelligent automation",
    icon: "/icons/code-merge.svg",
    iconAlt: "AI Support for Teams",
  },
];

const FocusAreas: React.FC<FocusAreasProps> = ({
  title = "Focus Areas",
  subtitle = "These areas represent the core of my professional practice. Each one supports the others, creating a comprehensive approach to software delivery.",
  focusAreas = fallbackFocusAreas,
}) => {
  const [sectionRef, isVisible] = useScrollAnimation();
  const [hasAnimated, setHasAnimated] = React.useState(false);

  React.useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible, hasAnimated]);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`w-full py-20 px-6 lg:px-8 bg-[#151e38] ${hasAnimated ? styles.sectionAnimation : ""}`}
      aria-label="Focus Areas"
    >
      <div className="mx-auto max-w-7xl flex flex-col gap-12">
        <header className={`mx-auto text-center max-w-3xl ${hasAnimated ? styles.headerAnimation : ""}`}>
          <h2 className="font-assistant font-semibold text-white text-5xl leading-[46px] mb-5">
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg leading-7 font-normal text-slate-300 max-w-xl mx-auto">
              {subtitle}
            </p>
          )}
        </header>

        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-7 items-start">
          {focusAreas.map((area, index) => (
            <article
              key={area.id}
              className={`flex flex-col w-full sm:w-[322px] min-h-[142px] items-start justify-start gap-2.5 px-6 py-6 bg-slate-900 rounded-[10px] border border-solid border-[#a78bfa80] ${hasAnimated ? styles.cardAnimation : ""}`}
              style={hasAnimated ? { animationDelay: `${0.2 + index * 0.1}s` } : undefined}
            >
              <div className="w-full relative flex items-start gap-4">
                {area.icon && (
                  <div className="flex-shrink-0">
                    <div className="w-11 h-11 bg-[#0ab3b81a] rounded-[10px] flex items-center justify-center">
                      {area.icon.endsWith('.svg') || area.icon.includes('.svg') ? (
                        <img
                          src={area.icon}
                          alt={area.iconAlt}
                          className="w-[25px] h-5"
                        />
                      ) : (
                        <Image
                          src={area.icon}
                          alt={area.iconAlt}
                          width={25}
                          height={20}
                          className="w-[25px] h-5"
                          unoptimized={area.icon.endsWith('.svg') || area.icon.includes('.svg')}
                        />
                      )}
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-inter font-bold text-white text-base leading-6 mb-2">
                    {area.title}
                  </h3>
                  <p className="font-inter font-normal text-slate-300 text-sm leading-6">
                    {area.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
};

export default FocusAreas;

